import { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import HeaderMaster from '../components/HeaderMaster';
import SideBarMaster from '../components/SideBarMaster';
import FooterNavigationMaster from '../components/FooterNavigationMaster';
import styles from './ItensMaster.module.css';
import { MdAdd, MdUploadFile, MdEdit, MdDelete } from "react-icons/md";
import { EstabelecimentosContext } from '../../../shared/contexts/EstabelecimentosContext';
import { CategoriasContext } from '../../../shared/contexts/CategoriasContext';
import { fetchWithAuth, getToken } from '../../../shared/utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Master consegue adicionar itens ao cardápio de QUALQUER estabelecimento —
// mesma lógica de /admin/cadastroprodutos (item por item ou importando um PDF
// de cardápio via IA), só que aqui quem escolhe a loja de destino é o master,
// em vez de vir implícito do login (localStorage.estabelecimento_id, que o
// master não tem porque não é dono de nenhuma loja).
function ItensMaster() {
    const { todosEstabelecimentos, fetchTodosEstabelecimentos } = useContext(EstabelecimentosContext);
    const { categorias, criarCategoria } = useContext(CategoriasContext);
    const [searchParams] = useSearchParams();

    // Vindo de GerenciarEstabelecimento.jsx (?estabelecimento_id=X), já chega
    // com a loja certa selecionada em vez de pedir pra escolher de novo.
    const [estabelecimentoId, setEstabelecimentoId] = useState(searchParams.get('estabelecimento_id') || '');
    const [itens, setItens] = useState([]);
    const [carregandoItens, setCarregandoItens] = useState(false);

    const [mostrarFormItem, setMostrarFormItem] = useState(false);
    const [mostrarImportarPdf, setMostrarImportarPdf] = useState(false);
    const [itemEditando, setItemEditando] = useState(null);

    useEffect(() => {
        fetchTodosEstabelecimentos();
    }, []);

    const carregarItens = async (idEstab) => {
        if (!idEstab) {
            setItens([]);
            return;
        }
        try {
            setCarregandoItens(true);
            const res = await fetch(`${API_URL}/cadastroprodutos?id_estabelecimento=${idEstab}`, { cache: 'no-store' });
            const data = await res.json();
            setItens(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Erro ao carregar itens do estabelecimento:', error);
            setItens([]);
        } finally {
            setCarregandoItens(false);
        }
    };

    useEffect(() => {
        carregarItens(estabelecimentoId);
    }, [estabelecimentoId]);

    const nomeEstabelecimentoSelecionado = todosEstabelecimentos.find(
        (e) => String(e.id) === String(estabelecimentoId)
    )?.nome_lanchonete;

    const excluirItem = async (item) => {
        if (!window.confirm(`Excluir "${item.nome_item}" do cardápio? Essa ação não pode ser desfeita.`)) return;

        try {
            const res = await fetchWithAuth(`${API_URL}/cadastroprodutos/${item.id_item}`, { method: 'DELETE' });
            if (!res) return;

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.error || 'Erro ao excluir produto.');
                return;
            }

            carregarItens(estabelecimentoId);
        } catch (error) {
            alert(`Erro na requisição: ${error.message}`);
        }
    };

    const alternarDisponibilidade = async (item) => {
        const disponivelAtual = item.disponivel?.toLowerCase() === 'sim';
        try {
            const res = await fetchWithAuth(`${API_URL}/atualizar-item-produto`, {
                method: 'POST',
                body: JSON.stringify({
                    id_item: item.id_item,
                    nome_item: item.nome_item,
                    descricao: item.descricao,
                    preco: item.preco,
                    id_categoria: item.id_categoria,
                    quantidade: item.quantidade,
                    disponivel: disponivelAtual ? 'nao' : 'sim'
                })
            });
            if (!res) return;

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.message || 'Erro ao atualizar disponibilidade.');
                return;
            }

            carregarItens(estabelecimentoId);
        } catch (error) {
            alert(`Erro na requisição: ${error.message}`);
        }
    };

    return (
        <div className={styles.secaoPrincipal}>
            <SideBarMaster />
            <div className={styles.containerDashboard}>
                <HeaderMaster />

                <div className={styles.contentDashboard}>
                    <div className={styles.tituloSecao}>
                        <div>
                            <h2>Itens do Cardápio</h2>
                            <p>Adicione produtos ao cardápio de qualquer estabelecimento, um por um ou importando um PDF.</p>
                        </div>
                    </div>

                    <div className={styles.seletorCard}>
                        <label htmlFor="seletor-estabelecimento">Estabelecimento</label>
                        <select
                            id="seletor-estabelecimento"
                            className={styles.seletorEstabelecimento}
                            value={estabelecimentoId}
                            onChange={(e) => setEstabelecimentoId(e.target.value)}
                        >
                            <option value="">Selecione um estabelecimento...</option>
                            {todosEstabelecimentos.map((est) => (
                                <option key={est.id} value={est.id}>
                                    {est.nome_lanchonete} {est.ativo ? '' : '(inativo)'}
                                </option>
                            ))}
                        </select>
                    </div>

                    {!estabelecimentoId ? (
                        <div className={styles.tableContainer}>
                            <p className={styles.estadoVazio}>
                                Selecione um estabelecimento acima para ver e adicionar itens do cardápio.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.acoesBar}>
                                <button
                                    className={styles.btnAcaoPrimaria}
                                    onClick={() => setMostrarFormItem(true)}
                                >
                                    <MdAdd size={18} /> Adicionar Item
                                </button>
                                <button
                                    className={styles.btnAcaoSecundaria}
                                    onClick={() => setMostrarImportarPdf(true)}
                                >
                                    <MdUploadFile size={18} /> Importar Cardápio via PDF
                                </button>
                            </div>

                            <div className={styles.tableContainer}>
                                {carregandoItens ? (
                                    <p className={styles.loading}>Carregando itens...</p>
                                ) : itens.length === 0 ? (
                                    <p className={styles.noData}>
                                        {nomeEstabelecimentoSelecionado} ainda não tem nenhum item cadastrado.
                                    </p>
                                ) : (
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Nome</th>
                                                <th>Preço</th>
                                                <th>Estoque</th>
                                                <th>Disponível</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itens.map((item) => (
                                                <tr key={item.id_item}>
                                                    <td>{item.nome_item}</td>
                                                    <td>R$ {Number(item.preco).toFixed(2)}</td>
                                                    <td>{item.quantidade === null || item.quantidade === undefined ? 'Ilimitado' : item.quantidade}</td>
                                                    <td>
                                                        {item.disponivel?.toLowerCase() === 'sim' ? (
                                                            <span className={styles.badgeDisponivel}>Disponível</span>
                                                        ) : (
                                                            <span className={styles.badgeIndisponivel}>Indisponível</span>
                                                        )}
                                                        <br />
                                                        <button className={styles.linkToggle} onClick={() => alternarDisponibilidade(item)}>
                                                            {item.disponivel?.toLowerCase() === 'sim' ? 'marcar indisponível' : 'marcar disponível'}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button className={styles.btnIcone} title="Editar" onClick={() => setItemEditando(item)}>
                                                            <MdEdit />
                                                        </button>
                                                        <button className={styles.btnIconePerigo} title="Excluir" onClick={() => excluirItem(item)}>
                                                            <MdDelete />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {mostrarFormItem && (
                <FormAdicionarItem
                    idEstabelecimento={estabelecimentoId}
                    categorias={categorias}
                    criarCategoria={criarCategoria}
                    onClose={() => setMostrarFormItem(false)}
                    onSucesso={() => {
                        setMostrarFormItem(false);
                        carregarItens(estabelecimentoId);
                    }}
                />
            )}

            {mostrarImportarPdf && (
                <ModalImportarPdf
                    idEstabelecimento={estabelecimentoId}
                    onClose={() => setMostrarImportarPdf(false)}
                    onConcluido={() => carregarItens(estabelecimentoId)}
                />
            )}

            {itemEditando && (
                <FormEditarItem
                    item={itemEditando}
                    categorias={categorias}
                    onClose={() => setItemEditando(null)}
                    onSucesso={() => {
                        setItemEditando(null);
                        carregarItens(estabelecimentoId);
                    }}
                />
            )}

            <FooterNavigationMaster />
        </div>
    );
}

// ===================== Formulário: adicionar item um por um ===================== //
// Mesma lógica/campos de CadastrarProdutoPopUp (shared/components), mas
// recebendo o estabelecimento de destino por prop em vez de ler
// localStorage.estabelecimento_id — o master não tem uma loja "própria".
function FormAdicionarItem({ idEstabelecimento, categorias, criarCategoria, onClose, onSucesso }) {
    const [nomeItem, setNomeItem] = useState('');
    const [descricao, setDescricao] = useState('');
    const [preco, setPreco] = useState('');
    const [precoFormatado, setPrecoFormatado] = useState('');
    const [idCategoria, setIdCategoria] = useState('');
    const [novaCategoriaMode, setNovaCategoriaMode] = useState(false);
    const [novaCategoriaNome, setNovaCategoriaNome] = useState('');
    const [imagem, setImagem] = useState('');
    const [disponivel, setDisponivel] = useState(true);
    const [quantidade, setQuantidade] = useState('');
    const [salvando, setSalvando] = useState(false);

    const handlePrecoChange = (e) => {
        const valor = e.target.value.replace(/\D/g, '');
        if (!valor) {
            setPreco('');
            setPrecoFormatado('');
            return;
        }
        const reais = (parseInt(valor, 10) / 100).toFixed(2);
        setPreco(reais);
        setPrecoFormatado(`R$ ${reais.replace('.', ',')}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!nomeItem || !preco || (!idCategoria && !novaCategoriaMode) || (novaCategoriaMode && !novaCategoriaNome.trim())) {
            alert('Preencha os campos obrigatórios (Nome, Preço e Seção)!');
            return;
        }

        try {
            setSalvando(true);
            let categoriaId = idCategoria;

            if (novaCategoriaMode && novaCategoriaNome.trim()) {
                const novaCat = await criarCategoria(novaCategoriaNome.trim());
                if (!novaCat) {
                    setSalvando(false);
                    return;
                }
                categoriaId = novaCat.id_categoria;
            }

            const res = await fetchWithAuth(`${API_URL}/cadastroprodutos`, {
                method: 'POST',
                body: JSON.stringify({
                    nome_item: nomeItem,
                    descricao,
                    preco,
                    id_categoria: categoriaId,
                    imagem,
                    disponivel,
                    quantidade: quantidade === '' ? null : Number(quantidade),
                    id_estabelecimento: idEstabelecimento
                })
            });

            if (!res) return; // sessão expirada, fetchWithAuth já tratou

            if (!res.ok) {
                const erro = await res.text();
                alert(`Erro ao cadastrar o item: ${erro}`);
                return;
            }

            alert('Item cadastrado com sucesso!');
            onSucesso();
        } catch (error) {
            alert(`Erro na requisição: ${error.message}`);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} onClick={onClose}>×</button>
                <h3 className={styles.modalTitle}>Adicionar Item</h3>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label>Nome do Item</label>
                        <input
                            type="text"
                            placeholder="Ex: X-Burguer"
                            value={nomeItem}
                            onChange={(e) => setNomeItem(e.target.value)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Descrição</label>
                        <input
                            type="text"
                            placeholder="Breve descrição do produto"
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Preço</label>
                        <input
                            type="text"
                            placeholder="R$ 0,00"
                            value={precoFormatado}
                            onChange={handlePrecoChange}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Seção</label>
                        {!novaCategoriaMode ? (
                            <select
                                value={idCategoria}
                                onChange={(e) => {
                                    if (e.target.value === '__nova__') {
                                        setNovaCategoriaMode(true);
                                        setIdCategoria('');
                                    } else {
                                        setIdCategoria(e.target.value);
                                    }
                                }}
                            >
                                <option value="">Selecione uma seção</option>
                                {categorias.map((cat) => (
                                    <option key={cat.id_categoria} value={cat.id_categoria}>
                                        {cat.nome_categoria}
                                    </option>
                                ))}
                                <option value="__nova__">+ Criar nova seção...</option>
                            </select>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    placeholder="Digite o nome da nova seção"
                                    value={novaCategoriaNome}
                                    onChange={(e) => setNovaCategoriaNome(e.target.value)}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    className={styles.linkVoltar}
                                    onClick={() => { setNovaCategoriaMode(false); setNovaCategoriaNome(''); }}
                                >
                                    Voltar para lista
                                </button>
                            </>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label>Disponibilidade</label>
                        <div className={styles.radioGroup}>
                            <label>
                                <input type="radio" checked={disponivel === true} onChange={() => setDisponivel(true)} />
                                Disponível
                            </label>
                            <label>
                                <input type="radio" checked={disponivel === false} onChange={() => setDisponivel(false)} />
                                Indisponível
                            </label>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Quantidade em Estoque</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="Deixe em branco para estoque ilimitado"
                            value={quantidade}
                            onChange={(e) => setQuantidade(e.target.value)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>URL da Imagem (opcional)</label>
                        <input
                            type="text"
                            placeholder="Cole a URL da imagem aqui"
                            value={imagem}
                            onChange={(e) => setImagem(e.target.value)}
                        />
                    </div>

                    <button className={styles.btnSalvar} type="submit" disabled={salvando}>
                        {salvando ? 'Salvando...' : 'Adicionar Item'}
                    </button>
                </form>
            </div>
        </div>
    );
}

// ===================== Formulário: editar item existente ===================== //
// Mesmos campos de FormAdicionarItem, mas pré-preenchidos e enviando pra
// /atualizar-item-produto (PUT lógico) em vez de criar um item novo.
function FormEditarItem({ item, categorias, onClose, onSucesso }) {
    const [nomeItem, setNomeItem] = useState(item.nome_item || '');
    const [descricao, setDescricao] = useState(item.descricao || '');
    const [preco, setPreco] = useState(item.preco || '');
    const [idCategoria, setIdCategoria] = useState(item.id_categoria || '');
    const [disponivel, setDisponivel] = useState(item.disponivel?.toLowerCase() !== 'nao');
    const [quantidade, setQuantidade] = useState(item.quantidade === null || item.quantidade === undefined ? '' : item.quantidade);
    const [salvando, setSalvando] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!nomeItem || !preco || !idCategoria) {
            alert('Preencha os campos obrigatórios (Nome, Preço e Seção)!');
            return;
        }

        try {
            setSalvando(true);

            const res = await fetchWithAuth(`${API_URL}/atualizar-item-produto`, {
                method: 'POST',
                body: JSON.stringify({
                    id_item: item.id_item,
                    nome_item: nomeItem,
                    descricao,
                    preco,
                    id_categoria: idCategoria,
                    disponivel: disponivel ? 'sim' : 'nao',
                    quantidade: quantidade === '' ? null : Number(quantidade)
                })
            });

            if (!res) return;

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.message || 'Erro ao atualizar o item.');
                return;
            }

            alert('Item atualizado com sucesso!');
            onSucesso();
        } catch (error) {
            alert(`Erro na requisição: ${error.message}`);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} onClick={onClose}>×</button>
                <h3 className={styles.modalTitle}>Editar Item</h3>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label>Nome do Item</label>
                        <input type="text" value={nomeItem} onChange={(e) => setNomeItem(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Descrição</label>
                        <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Preço</label>
                        <input type="number" step="0.01" min="0" value={preco} onChange={(e) => setPreco(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Seção</label>
                        <select value={idCategoria} onChange={(e) => setIdCategoria(e.target.value)}>
                            <option value="">Selecione uma seção</option>
                            {categorias.map((cat) => (
                                <option key={cat.id_categoria} value={cat.id_categoria}>
                                    {cat.nome_categoria}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Disponibilidade</label>
                        <div className={styles.radioGroup}>
                            <label>
                                <input type="radio" checked={disponivel === true} onChange={() => setDisponivel(true)} />
                                Disponível
                            </label>
                            <label>
                                <input type="radio" checked={disponivel === false} onChange={() => setDisponivel(false)} />
                                Indisponível
                            </label>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Quantidade em Estoque</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="Deixe em branco para estoque ilimitado"
                            value={quantidade}
                            onChange={(e) => setQuantidade(e.target.value)}
                        />
                    </div>

                    <button className={styles.btnSalvar} type="submit" disabled={salvando}>
                        {salvando ? 'Salvando...' : 'Salvar alterações'}
                    </button>
                </form>
            </div>
        </div>
    );
}

// ===================== Modal: importar cardápio via PDF ===================== //
// Mesmo fluxo de CadastroProdutos.jsx (extrai com IA em /extract-cardapio e
// cadastra item por item em /cadastroprodutos), retido ao estabelecimento
// escolhido pelo master.
function ModalImportarPdf({ idEstabelecimento, onClose, onConcluido }) {
    const [arquivoPdf, setArquivoPdf] = useState(null);
    const [processando, setProcessando] = useState(false);
    const [resultado, setResultado] = useState('');
    const fileInputRef = useRef(null);

    const handleArquivo = (e) => {
        const arquivo = e.target.files[0];
        if (arquivo && arquivo.type === 'application/pdf') {
            setArquivoPdf(arquivo);
            setResultado('');
        } else {
            alert('Por favor, selecione um arquivo PDF válido!');
        }
    };

    const processarPdf = async () => {
        if (!arquivoPdf || !idEstabelecimento) return;

        try {
            setProcessando(true);
            setResultado('');

            const formData = new FormData();
            formData.append('pdf', arquivoPdf);

            const token = getToken();
            const response = await fetch(`${API_URL}/extract-cardapio`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
                throw new Error(errorData.error || errorData.details || 'Erro ao processar PDF');
            }

            const data = await response.json();
            const produtosExtraidos = data.produtos;

            if (!produtosExtraidos || produtosExtraidos.length === 0) {
                setResultado('Nenhum produto foi encontrado no PDF.');
                return;
            }

            let sucessos = 0;
            let erros = 0;

            for (const produto of produtosExtraidos) {
                try {
                    const resCadastro = await fetchWithAuth(`${API_URL}/cadastroprodutos`, {
                        method: 'POST',
                        body: JSON.stringify({
                            nome_item: produto.nome || produto.name,
                            descricao: produto.descricao || produto.description || '',
                            preco: produto.preco || produto.price || 0,
                            id_categoria: produto.id_categoria || null,
                            imagem: '',
                            disponivel: true,
                            // Cardápio importado não traz contagem de estoque — deixa
                            // ilimitado (null) em vez de 0 (que apareceria "em falta").
                            quantidade: null,
                            id_estabelecimento: idEstabelecimento
                        })
                    });

                    if (resCadastro && resCadastro.ok) {
                        sucessos++;
                    } else {
                        erros++;
                    }
                } catch (erroItem) {
                    console.error('Erro ao cadastrar produto do PDF:', erroItem);
                    erros++;
                }
            }

            setResultado(`Processamento concluído!\nProdutos cadastrados: ${sucessos}\nErros: ${erros}`);
            onConcluido();
            setArquivoPdf(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            console.error('Erro ao processar PDF:', error);
            setResultado(`Erro ao processar o arquivo PDF: ${error.message || 'tente novamente.'}`);
        } finally {
            setProcessando(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} onClick={onClose}>×</button>
                <h3 className={styles.modalTitle}>Importar Cardápio via PDF</h3>
                <p className={styles.modalSubtitle}>
                    Envie o cardápio em PDF para extrair e cadastrar todos os produtos automaticamente.
                </p>

                <div className={styles.pdfInputGroup}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        onChange={handleArquivo}
                        className={styles.pdfFileInput}
                        disabled={processando}
                    />
                    {arquivoPdf && (
                        <span className={styles.pdfFileName}>Arquivo selecionado: {arquivoPdf.name}</span>
                    )}
                </div>

                <button
                    type="button"
                    className={styles.btnSalvar}
                    onClick={processarPdf}
                    disabled={!arquivoPdf || processando}
                >
                    {processando ? 'Processando PDF...' : 'Processar e Cadastrar Produtos'}
                </button>

                {resultado && <div className={styles.pdfResultado}>{resultado}</div>}
            </div>
        </div>
    );
}

export default ItensMaster;
